import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calculator, CreditCard, IndianRupee, KeyRound, Lock, Mail, ReceiptText, ShieldCheck, Sparkles, Store, WalletCards, X } from "lucide-react";
import { toast } from "sonner";
import MacJitLogo from "../components/MacJitLogo";
import { useAuth } from "../context/AuthContext";
import { moduleHome } from "../lib/subscriptionModules";
import { roleHomePath } from "../lib/roles";

export default function MiniLoginPage() {
  const { login, changePassword } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const seed = searchParams.get("staff") || "";
  const [identifier, setIdentifier] = useState(seed);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [forceReset, setForceReset] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  const formatPhone = (value) => {
    const trimmed = String(value || "").trim();
    if (!trimmed || trimmed.includes("@") || /[a-zA-Z]/.test(trimmed)) return trimmed.toLowerCase();
    const digits = trimmed.replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("91") && digits.length > 10) return `+${digits.slice(0, 12)}`;
    return `+91${digits.slice(0, 10)}`;
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!identifier.trim() || !password) return toast.error("Enter Mini login and password");
    setBusy(true);
    try {
      const { user, mustReset } = await login(identifier.trim(), password);
      if (mustReset) {
        setForceReset({ user });
        toast.message("Set a new password to continue");
      } else {
        toast.success(`Welcome, ${user.name}`);
        nav(moduleHome(user) || roleHomePath(user.role));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Mini login failed");
    } finally {
      setBusy(false);
    }
  };

  const submitNewPassword = async (e) => {
    e?.preventDefault();
    if (newPwd.length < 8) return toast.error("New password must be at least 8 characters");
    if (newPwd !== confirmPwd) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await changePassword(password, newPwd);
      toast.success("Password updated");
      const u = forceReset?.user;
      setForceReset(null);
      if (u) nav(moduleHome(u) || roleHomePath(u.role));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080604] text-zinc-100">
      <div className="relative min-h-screen overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(249,115,22,0.22),transparent_30%),radial-gradient(circle_at_88%_20%,rgba(34,197,94,0.12),transparent_24%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[size:28px_28px]" />
        <div className="relative mx-auto grid min-h-screen max-w-7xl lg:grid-cols-[1.05fr_0.95fr]">
          <section className="flex min-h-[46vh] flex-col justify-between px-5 py-6 sm:px-8 lg:min-h-screen lg:py-10">
            <div>
              <button onClick={() => nav("/")} className="mb-8 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-orange-500">
                <ArrowLeft className="h-3 w-3" /> MacJit
              </button>
              <div className="flex items-center gap-3">
                <MacJitLogo size={42} />
                <div>
                  <p className="font-display text-2xl font-black tracking-tight"><span>MINI</span><span className="text-orange-500"> / POS</span></p>
                  <p className="font-mono text-[9px] uppercase tracking-[0.28em] text-orange-400">Counter billing console</p>
                </div>
              </div>
            </div>

            <div className="my-10 max-w-2xl">
              <p className="mb-4 inline-flex items-center gap-2 border border-orange-500/40 bg-orange-500/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.28em] text-orange-300">
                <Sparkles className="h-3 w-3" /> Mini plan family
              </p>
              <h1 className="font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter sm:text-7xl">
                Bill fast.<br />
                Collect clean.<br />
                <span className="text-orange-500">Close daily.</span>
              </h1>
              <p className="mt-6 max-w-xl text-sm leading-relaxed text-zinc-400">
                Mini is the focused billing counter for shops: create GST-ready bills, collect by cash or Razorpay, send payment links, track credit sales, refunds, archives, and storage from one lightweight workspace.
              </p>
            </div>

            <div className="grid gap-px bg-zinc-800 sm:grid-cols-3">
              {[
                { icon: ReceiptText, label: "Bills", value: "Multi item" },
                { icon: CreditCard, label: "Payments", value: "Razorpay" },
                { icon: ShieldCheck, label: "Records", value: "Audited" },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="bg-black/75 p-4 backdrop-blur">
                  <Icon className="mb-3 h-4 w-4 text-orange-500" />
                  <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">{label}</p>
                  <p className="font-display text-lg font-black uppercase tracking-tight">{value}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="flex items-center px-5 pb-10 sm:px-8 lg:px-10 lg:py-10">
            <div className="w-full border border-zinc-800 bg-zinc-950/92 p-5 shadow-2xl shadow-black/50 sm:p-8">
              <div className="mb-7 flex items-start justify-between gap-4">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Mini sign in</p>
                  <h2 className="mt-2 font-display text-3xl font-black uppercase tracking-tight">Merchant desk</h2>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-500">For Mini owners and counter billing access.</p>
                </div>
                <div className="grid h-12 w-12 place-items-center bg-orange-500 text-black">
                  <Calculator className="h-6 w-6" />
                </div>
              </div>

              <div className="mb-6 grid grid-cols-3 gap-px bg-zinc-800">
                <MiniStat icon={IndianRupee} label="Counter" value="Bill" />
                <MiniStat icon={WalletCards} label="Credit" value="Track" />
                <MiniStat icon={Store} label="Shop" value="1 seat" />
              </div>

              <form onSubmit={submit} className="space-y-4">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Registered mobile or email</span>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                    <input
                      data-testid="mini-login-identifier"
                      type="text"
                      inputMode="text"
                      autoComplete="username"
                      spellCheck={false}
                      required
                      value={identifier}
                      onChange={(e) => setIdentifier(formatPhone(e.target.value))}
                      placeholder="+91... or owner@email.com"
                      className="w-full border border-zinc-800 bg-black py-3 pl-10 pr-4 font-mono text-white outline-none focus:border-orange-500"
                    />
                  </div>
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Password</span>
                  <div className="relative mt-2">
                    <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
                    <input
                      data-testid="mini-login-password"
                      type="password"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mini password"
                      className="w-full border border-zinc-800 bg-black py-3 pl-10 pr-4 font-mono text-white outline-none focus:border-orange-500"
                    />
                  </div>
                </label>
                <button data-testid="mini-login-submit" disabled={busy} className="flex w-full items-center justify-center gap-2 bg-orange-500 py-4 font-display text-sm font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
                  {busy ? "Opening Mini..." : <>Open Mini <ArrowRight className="h-4 w-4" /></>}
                </button>
                <div className="grid gap-2 sm:grid-cols-2">
                  <button type="button" onClick={() => { setForgotEmail(identifier.includes("@") ? identifier : ""); setForgotOpen(true); }} className="border border-zinc-800 py-3 font-display text-xs font-black uppercase tracking-widest text-zinc-300 hover:border-orange-500 hover:text-orange-500">
                    Forgot password
                  </button>
                  <button type="button" onClick={() => nav("/terminal")} className="border border-zinc-800 py-3 font-display text-xs font-black uppercase tracking-widest text-zinc-500 hover:border-zinc-500 hover:text-zinc-200">
                    Terminal Pro login
                  </button>
                </div>
              </form>
            </div>
          </section>
        </div>
      </div>

      {forceReset && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-4 backdrop-blur">
          <form onSubmit={submitNewPassword} className="w-full max-w-md border border-orange-500/50 bg-zinc-950 p-8 shadow-2xl shadow-orange-950/30">
            <div className="mb-5 flex items-center gap-3">
              <KeyRound className="h-6 w-6 text-orange-500" />
              <h3 className="font-display text-2xl font-black uppercase tracking-tight text-white">Set Mini password</h3>
            </div>
            <p className="mb-5 text-sm leading-relaxed text-zinc-400">You are using a temporary password. Set a new one before entering Mini.</p>
            <input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} required minLength={8} placeholder="New password" className="mb-3 w-full border border-zinc-800 bg-black px-4 py-3 font-mono text-white outline-none focus:border-orange-500" />
            <input type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} required minLength={8} placeholder="Confirm password" className="mb-5 w-full border border-zinc-800 bg-black px-4 py-3 font-mono text-white outline-none focus:border-orange-500" />
            <button disabled={busy} className="w-full bg-orange-500 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
              {busy ? "Saving..." : "Update password"}
            </button>
          </form>
        </div>
      )}

      {forgotOpen && <MiniForgotPasswordModal email={forgotEmail} setEmail={setForgotEmail} busy={busy} setBusy={setBusy} onClose={() => setForgotOpen(false)} />}
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="bg-black p-3">
      <Icon className="mb-2 h-4 w-4 text-orange-500" />
      <p className="font-mono text-[8px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="font-display text-lg font-black uppercase tracking-tight text-white">{value}</p>
    </div>
  );
}

function MiniForgotPasswordModal({ email, setEmail, busy, setBusy, onClose }) {
  const { requestPasswordReset } = useAuth();
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await requestPasswordReset(email.trim().toLowerCase());
      toast.success("If the email exists, reset link has been sent");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reset link");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-4 backdrop-blur">
      <form onSubmit={submit} className="w-full max-w-md border border-orange-500/40 bg-zinc-950 p-7 shadow-2xl shadow-black/50">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-500">Mini recovery</p>
            <h3 className="mt-2 font-display text-3xl font-black uppercase tracking-tight text-white">Reset password</h3>
          </div>
          <button type="button" onClick={onClose} className="grid h-9 w-9 place-items-center border border-zinc-800 text-zinc-400 hover:border-orange-500 hover:text-orange-500" aria-label="Close reset">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-zinc-400">Enter the registered Mini owner email. We will send a secure reset link.</p>
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value.toLowerCase())} placeholder="owner@example.com" className="mt-5 w-full border border-zinc-800 bg-black px-4 py-3 font-mono text-white outline-none focus:border-orange-500" />
        <button disabled={busy} className="mt-5 w-full bg-orange-500 py-3 font-display font-black uppercase tracking-widest text-black hover:bg-orange-400 disabled:opacity-50">
          {busy ? "Sending..." : "Send reset link"}
        </button>
      </form>
    </div>
  );
}
